import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button } from '../../components/ui'
import { deleteMedia, mediaURL, uploadMedia } from '../../api/objects-write'
import type { MediaAsset } from '../../api/objects'
import { t } from '../../i18n'
import { MediaViewer } from './MediaViewer'

/**
 * The photographs and videos of one offer.
 *
 * Files are served through the process rather than from a static directory: it
 * is somebody's flat behind a login, and a URL that worked without a session
 * would be one anybody could guess their way to.
 *
 * There is no ordering and no cover to pick — the owner asked for neither. The
 * first upload is what the list screen shows, which is enough to recognise your
 * own object at a glance and costs nobody a decision.
 */
export function MediaPanel({
  listingID,
  propertyID,
  assets,
}: {
  readonly listingID: string
  readonly propertyID: string
  readonly assets: readonly MediaAsset[]
}) {
  const [failure, setFailure] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  // How far a batch has got, so that twenty files are not twenty blank seconds.
  const [progress, setProgress] = useState<{ readonly done: number; readonly total: number } | null>(
    null,
  )
  // Which file is open full-size, if any.
  const [viewing, setViewing] = useState<number | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  const client = useQueryClient()
  const refresh = () => client.invalidateQueries({ queryKey: ['property', propertyID] })

  // A whole batch, not a file.
  //
  // Photographing a flat produces twenty pictures at once, and one-file-at-a-time
  // meant twenty file dialogs and twenty full refetches of the card between
  // phone calls. They go up one after another rather than at once -- the server
  // writes them to disk and the account this runs beside is a personal one --
  // but the person chooses them once and the card is re-read once, at the end.
  const upload = useMutation({
    mutationFn: async (files: readonly File[]) => {
      const refused: string[] = []
      let mayNotPlay = false

      for (const [index, file] of files.entries()) {
        setProgress({ done: index, total: files.length })
        try {
          const stored = await uploadMedia(listingID, file)
          if (stored.may_not_play_everywhere) mayNotPlay = true
        } catch (error) {
          // One rejected file does not cancel the other nineteen; which ones
          // were refused is said at the end, by name.
          refused.push(`${file.name}: ${(error as Error).message}`)
        }
      }
      return { refused, mayNotPlay }
    },
    onSuccess: ({ refused, mayNotPlay }) => {
      setFailure(refused.length === 0 ? null : refused.join('\n'))
      // Said at the moment of upload rather than discovered later by a client:
      // nothing transcodes, and an iPhone's .mov does not open everywhere.
      setWarning(mayNotPlay ? t('media.mayNotPlay') : null)
      setProgress(null)
      void refresh()
    },
    onError: (error: Error) => {
      setProgress(null)
      setFailure(error.message)
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteMedia(id),
    onSuccess: () => {
      setFailure(null)
      void refresh()
    },
    onError: (error: Error) => setFailure(error.message),
  })

  return (
    <section className="media">
      <h4 className="media__title">{t('media.title')}</h4>

      <ul className="media__grid">
        {assets.map((asset, position) => (
          <li key={asset.id} className="media__item">
            {/* The thumbnail opens the file rather than trying to be it. A
                ninety-pixel box cannot show a kitchen and cannot play a video:
                its whole job is to be recognisable and clickable. */}
            <button
              type="button"
              className="media__open"
              onClick={() => setViewing(position)}
              aria-label={t('media.open')}
            >
              {asset.kind === 'video' ? (
                <span className="media__thumb media__thumb--video">
                  <video src={mediaURL(asset.id)} preload="metadata" muted />
                  <span className="media__play" aria-hidden="true">
                    ▶
                  </span>
                </span>
              ) : (
                <img
                  className="media__thumb"
                  src={mediaURL(asset.id)}
                  alt={asset.caption}
                  loading="lazy"
                />
              )}
            </button>
            <button
              type="button"
              className="media__remove"
              onClick={() => {
                if (window.confirm(t('media.removeConfirm'))) remove.mutate(asset.id)
              }}
              disabled={remove.isPending}
            >
              {t('media.remove')}
            </button>
          </li>
        ))}
      </ul>

      {assets.length === 0 ? <p className="media__empty">{t('media.empty')}</p> : null}

      <input
        ref={picker}
        type="file"
        className="media__picker"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/webm,video/quicktime,application/pdf"
        multiple
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          if (files.length > 0) upload.mutate(files)
          // Cleared so that choosing the same file twice fires again.
          event.target.value = ''
        }}
      />
      <Button variant="secondary" busy={upload.isPending} onClick={() => picker.current?.click()}>
        {upload.isPending
          ? progress === null || progress.total < 2
            ? t('media.uploading')
            : t('media.uploadingCount', {
                done: String(progress.done + 1),
                total: String(progress.total),
              })
          : t('media.add')}
      </Button>

      {warning === null ? null : <Alert tone="warning">{warning}</Alert>}
      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      {viewing === null ? null : (
        <MediaViewer
          assets={assets}
          index={viewing}
          onClose={() => setViewing(null)}
          onMove={setViewing}
        />
      )}
    </section>
  )
}
